import { ThemeOptions } from '@mui/material';
import { TonalPalette } from '../types/TonalPalette';
import { ThemeMode } from '../types/ThemeMode';
import { ThemeScheme } from '../types/ThemeScheme';

declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        displayLarge: true;
        displayMedium: true;
        displaySmall: true;
        headlineLarge: true;
        headlineMedium: true;
        headlineSmall: true;
        titleLarge: true;
        titleMedium: true;
        titleSmall: true;
        bodyLarge: true;
        bodyMedium: true;
        bodySmall: true;
        labelLarge: true;
        labelLargeProminent: true;
        labelMedium: true;
        labelMediumProminent: true;
        labelSmall: true;

        h1: false;
        h2: false;
        h3: false;
        h4: false;
        h5: false;
        h6: false;
        subtitle1: false;
        subtitle2: false;
        body1: false;
        body2: false;
        caption: false;
        overline: false;
        button: false;
    }
}

export const getMUITypography = (
    themeMode: ThemeMode,
    themescheme: ThemeScheme,
): ThemeOptions => {
    const scheme = themescheme[themeMode];
    return {
        typography: {
            fontFamily: 'Roboto, Noto Sans Hebrew, Arial, sans-serif',
            displayLarge: {
                fontWeight: 400,
                fontSize: '57px',
                letterSpacing: '-0.25px',
                lineHeight: '64px',
            },
            displayMedium: {
                fontWeight: 400,
                fontSize: '45px',
                lineHeight: '52px',
            },
            displaySmall: {
                fontWeight: 400,
                fontSize: '36px',
                lineHeight: '44px',
            },
            headlineLarge: {
                fontWeight: 400,
                fontSize: '32px',
                lineHeight: '40px',
            },
            headlineMedium: {
                fontWeight: 400,
                fontSize: '28px',
                lineHeight: '36px',
            },
            headlineSmall: {
                fontWeight: 400,
                fontSize: '24px',
                lineHeight: '32px',
            },
            titleLarge: {
                fontWeight: 400,
                fontSize: '22px',
                lineHeight: '28px',
            },
            titleMedium: {
                fontWeight: 500,
                fontSize: '16px',
                lineHeight: '24px',
            },
            titleSmall: {
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '20px',
            },
            bodyLarge: {
                fontWeight: 400,
                fontSize: '16px',
                letterSpacing: '0.5px',
                lineHeight: '24px',
            },
            bodyMedium: {
                fontWeight: 400,
                fontSize: '14px',
                letterSpacing: '0.25px',
                lineHeight: '20px',
            },
            bodySmall: {
                fontWeight: 400,
                fontSize: '12px',
                letterSpacing: '0.4px',
                lineHeight: '16px',
            },
            labelLarge: {
                fontWeight: 500,
                fontSize: '14px',
                letterSpacing: '0.1px',
                lineHeight: '20px',
            },
            labelLargeProminent: {
                fontWeight: 700,
                fontSize: '14px',
                letterSpacing: '0.1px',
                lineHeight: '20px',
            },
            labelMedium: {
                fontWeight: 500,
                fontSize: '12px',
                letterSpacing: '0.5px',
                lineHeight: '16px',
            },
            labelMediumProminent: {
                fontWeight: 700,
                fontSize: '12px',
                letterSpacing: '0.5px',
                lineHeight: '16px',
            },
            labelSmall: {
                fontWeight: 500,
                fontSize: '11px',
                letterSpacing: '0.5px',
                lineHeight: '16px',
            },

            h1: {
                fontSize: '1.875rem',
                lineHeight: '2.25rem',
                fontWeight: 700,
                marginBottom: '1.5rem',
            },
            h2: {
                fontSize: '1.5rem',
                lineHeight: '1.75rem',
                fontWeight: 700,
                marginBottom: '1.5rem',
            },
            h3: {
                fontSize: '1.25rem',
                lineHeight: '1.5rem',
                fontWeight: 700,
                marginBottom: '1.5rem',
            },
            h4: {
                fontSize: '1.125rem',
                lineHeight: '1.375rem',
                fontWeight: 700,
                marginBottom: '1.5rem',
            },
            h5: {
                fontSize: '1.125rem',
                lineHeight: '1.375rem',
                fontWeight: 600,
                color: scheme.onSurfaceVariant,
            },
            h6: {
                fontSize: '0.875rem',
                lineHeight: '1.125rem',
                fontWeight: 700,
                marginBottom: '1.5rem',
            },
            subtitle2: {
                color: scheme.onSurfaceVariant,
                fontWeight: 600,
            },
            body2: {
                color: scheme.outline,
            },
            caption: {
                color: scheme.outline,
            },
        },
        components: {
            MuiTypography: {
                defaultProps: {
                    variantMapping: {
                        displayLarge: 'h1',
                        displayMedium: 'h2',
                        displaySmall: 'h3',
                        headlineLarge: 'h4',
                        headlineMedium: 'h5',
                        headlineSmall: 'h6',
                        titleLarge: 'h2',
                        titleMedium: 'h3',
                        titleSmall: 'h4',
                        bodyLarge: 'p',
                        bodyMedium: 'p',
                        bodySmall: 'p',
                        labelLarge: 'p',
                        labelLargeProminent: 'p',
                        labelMedium: 'p',
                        labelMediumProminent: 'p',
                        labelSmall: 'p',
                    },
                },
                styleOverrides: {
                    noWrap: {
                        whiteSpace: 'nowrap',
                    },
                    bodySmall: {
                        whiteSpace: 'pre-line',
                    },
                    bodyMedium: {
                        whiteSpace: 'pre-line',
                    },
                    bodyLarge: {
                        whiteSpace: 'pre-line',
                    },
                },
            },
        },
        // mixins: {
        //     typography: {
        //         displayLarge: {
        //             fontWeight: 400,
        //             fontSize: '57px',
        //             letterSpacing: '-0.25px',
        //             lineHeight: '64px',
        //         },
        //     },
        // },
    };
};
